import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FieldRenderer } from '../components/FieldRenderer';
import { CollectionScheduleCard } from '../components/CollectionScheduleCard';
import { IdentityCard } from '../components/IdentityCard';
import { ReviewNotesCard } from '../components/ReviewNotesCard';
import { RaciAssignments } from '../components/RaciAssignments';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { DocumentManager } from '../components/DocumentManager';
import { RequirementsTracker } from '../components/RequirementsTracker';
import { assetsApi, departmentsApi, evidenceSubmissionsApi, linkedArtefactsApi, peopleApi, raciApi, registerConfigApi } from '../services/api';
import type { Asset, Department, EnabledModules, FieldConfig, FieldValues, Person } from '../types/models';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getMissingRequiredFields } from '../utils/fieldValidation';

type EntityApi = {
  getOne: (id: string) => Promise<any>;
  create: (data: any) => Promise<any>;
  update: (id: string, data: any) => Promise<any>;
};

// Fallback field set for kind='evidence' when a register's own stored FieldConfig is missing
// or incomplete - see groupedFields below for why this exists. Matches the real, complete field
// set confirmed live against a properly-configured evidence register (EVREG-cc3fc400) - same
// fieldIds/types/cards/options a normal evidence form actually has, not a guess.
export const DEFAULT_EVIDENCE_FIELDS: FieldConfig[] = [
  { fieldId: 'evidence_title', fieldName: 'Evidence Title', fieldType: 'text', cardNumber: 1, cardName: 'Identity', orderIndex: 1, isRequired: true, isEditable: true, isVisible: true },
  { fieldId: 'evidence_code', fieldName: 'Evidence Code', fieldType: 'text', cardNumber: 1, cardName: 'Identity', orderIndex: 2, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'type', fieldName: 'Evidence Type', fieldType: 'select', cardNumber: 1, cardName: 'Identity', orderIndex: 3, isRequired: false, isEditable: true, isVisible: true,
    options: ['Other', 'System', 'Policy', 'Sample', 'List (one-time)', 'List (review period)', 'Configuration', 'Not applicable', 'Example(s)', 'Template', 'General', 'Review'] },
  { fieldId: 'evidence_status', fieldName: 'Status', fieldType: 'select', cardNumber: 1, cardName: 'Identity', orderIndex: 4, isRequired: false, isEditable: true, isVisible: true,
    options: ['Active', 'Descoped'] },
  { fieldId: 'schedule_start_date', fieldName: 'Schedule Start Date', fieldType: 'date', cardNumber: 4, cardName: 'Collection Schedule', orderIndex: 0, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'collection_frequency', fieldName: 'Collection Frequency', fieldType: 'segmented', cardNumber: 4, cardName: 'Collection Schedule', orderIndex: 1, isRequired: false, isEditable: true, isVisible: true,
    options: ['Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually'] },
  { fieldId: 'grace_period_days', fieldName: 'Grace Period (days)', fieldType: 'number', cardNumber: 4, cardName: 'Collection Schedule', orderIndex: 2, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'evidence_last_reviewed', fieldName: 'Last Reviewed', fieldType: 'date', cardNumber: 4, cardName: 'Collection Schedule', orderIndex: 3, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'next_due_date', fieldName: 'Next Due Date', fieldType: 'date', cardNumber: 4, cardName: 'Collection Schedule', orderIndex: 4, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'schedule_status', fieldName: 'Schedule Status', fieldType: 'segmented', cardNumber: 4, cardName: 'Collection Schedule', orderIndex: 5, isRequired: false, isEditable: true, isVisible: true,
    options: ['On Track', 'Due Soon', 'Overdue', 'Not Scheduled'] },
  { fieldId: 'frequency_comment', fieldName: 'Frequency Comment', fieldType: 'textarea', cardNumber: 4, cardName: 'Collection Schedule', orderIndex: 6, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'comment', fieldName: 'Comment', fieldType: 'textarea', cardNumber: 5, cardName: 'Review Notes', orderIndex: 1, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'improvement_needed', fieldName: 'Improvement Needed', fieldType: 'textarea', cardNumber: 5, cardName: 'Review Notes', orderIndex: 2, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'evidence_uploaded_by', fieldName: 'Uploaded By', fieldType: 'text', cardNumber: 6, cardName: 'Metadata', orderIndex: 1, isRequired: false, isEditable: true, isVisible: true },
  { fieldId: 'evidence_date_uploaded', fieldName: 'Date Uploaded', fieldType: 'date', cardNumber: 6, cardName: 'Metadata', orderIndex: 2, isRequired: false, isEditable: true, isVisible: true },
];

type ExtraTab = {
  id: string;
  label: string;
  icon: string;
  content: (itemId: string | undefined) => React.ReactNode;
};

type Props = {
  kind: Parameters<typeof registerConfigApi.getFieldConfigs>[0];
  idParam: string;
  title: string;
  iconClass: string;
  api: EntityApi;
  extraTabs?: ExtraTab[];
};

export const RegisterItemDetail: React.FC<Props> = ({
  kind,
  idParam,
  title,
  iconClass,
  api,
  extraTabs = [],
}) => {
  const params = useParams();
  const itemId = (params as Record<string, string | undefined>)[idParam];

  const [searchParams] = useSearchParams();
  const registerId = searchParams.get('registerId') || '';
  const autoLinkControlId = searchParams.get('autoLinkControlId') || '';
  const autoLinkControlRegister = searchParams.get('autoLinkControlRegister') || '';
  const autoLinkRiskId = searchParams.get('autoLinkRiskId') || '';
  const autoLinkRiskRegister = searchParams.get('autoLinkRiskRegister') || '';

  const { getTenantId } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  // New item, or the list's Edit action (?edit=true), both start in edit mode - see RiskDetail.tsx.
  const [isEditing, setIsEditing] = useState(!itemId || searchParams.get('edit') === 'true');
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'details');
  const [invalidFieldIds, setInvalidFieldIds] = useState<Set<string>>(new Set());

  const {
    data: fieldConfigs = [],
    isLoading: isLoadingConfigs,
    error: configsError,
  } = useQuery({
    queryKey: ['fieldConfigs', kind, registerId],
    queryFn: () => registerConfigApi.getFieldConfigs(kind, registerId),
    enabled: !!registerId,
    select: (data) => {
      if (!Array.isArray(data)) return data;
      
      // Deep sanitization helper
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return data.map(sanitizeObject);
    },
  });

  const { data: cardDefinitions = {} } = useQuery({
    queryKey: ['cardDefinitions', kind, registerId],
    queryFn: () => registerConfigApi.getCardDefinitions(kind, registerId),
    enabled: !!registerId,
    select: (data) => {
      if (!data || typeof data !== 'object') return data;
      
      // Deep sanitization helper
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  const { data: enabledModules = {
    documentManager: true,
    raciManager: true,
    linkedArtefacts: true,
    requirementsTracker: true,
  } } = useQuery<EnabledModules>({
    queryKey: ['moduleConfig', kind, registerId],
    queryFn: () => registerConfigApi.getModuleConfig(kind, registerId),
    enabled: !!registerId,
  });

  const {
    data: item,
    isLoading: isLoadingItem,
    error: itemError,
  } = useQuery({
    queryKey: [kind, itemId],
    queryFn: () => api.getOne(itemId!),
    enabled: !!itemId,
    select: (data) => {
      if (!data) return data;
      
      // Deep sanitization helper
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return sanitizeObject(data);
    },
  });

  const { data: assets = [] } = useQuery<Asset[]>({
    queryKey: ['assets'],
    queryFn: assetsApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);
        
        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }
        
        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };
      
      return data.map(sanitizeObject);
    },
  });

  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
    select: (data) => {
      const sanitizeObject = (obj: any): any => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(sanitizeObject);

        // If it's a {value, label} object, return just the value
        if ('value' in obj && 'label' in obj && Object.keys(obj).length === 2) {
          return obj.value;
        }

        // Otherwise recursively sanitize all properties
        const result: any = {};
        Object.keys(obj).forEach((key) => {
          result[key] = sanitizeObject(obj[key]);
        });
        return result;
      };

      return data.map(sanitizeObject);
    },
  });

  const { data: heroRaci = [] } = useQuery<any[]>({
    queryKey: ['raci', kind, itemId],
    queryFn: () => raciApi.getByArtefact(kind, itemId!),
    enabled: kind === 'evidence' && !!itemId,
  });

  const accountablePerson = useMemo(() => {
    const assignment = (heroRaci as any[]).find((r) => r.role === 'A');
    if (!assignment) return null;
    return (people as Person[]).find((p) => p.personId === assignment.personId) || null;
  }, [heroRaci, people]);

  const { data: submissionsData } = useQuery<any>({
    queryKey: ['evidenceSubmissions', itemId],
    queryFn: () => evidenceSubmissionsApi.list(itemId!),
    enabled: kind === 'evidence' && !!itemId,
  });
  const submissionsCount: number | null = kind === 'evidence' ? (submissionsData?.submissions?.length ?? null) : null;
  const evidenceSubmissions: any[] = kind === 'evidence' ? (submissionsData?.submissions ?? []) : [];

  useEffect(() => {
    // Handle nested structure: item = {issue: {...}} or {incident: {...}}
    const itemData = item?.[kind] || item;
    
    if (itemData && typeof itemData === 'object') {
      // Fields can be stored in two places:
      // 1. Nested under 'fields' object (dynamic fields from Lambda)
      // 2. At root level (legacy fields and some field types)
      const metadataKeys = ['issueId', 'incidentId', 'remediationId', 'threatId', 'issue_id', 'incident_id', 'remediation_id', 'threat_id', 'tenantId', 'registerId', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'fields'];
      
      const sanitizedFields: FieldValues = {};
      
      // First, extract nested fields from the 'fields' object if it exists
      if (itemData.fields && typeof itemData.fields === 'object') {
        Object.keys(itemData.fields).forEach((key) => {
          const val = itemData.fields[key];
          if (val && typeof val === 'object' && 'value' in val) {
            sanitizedFields[key] = val.value;
          } else {
            sanitizedFields[key] = val;
          }
        });
      }
      
      // Then, extract root-level fields (these override nested fields if same key)
      Object.keys(itemData).forEach((key) => {
        if (metadataKeys.includes(key)) return; // Skip metadata fields
        
        const val = itemData[key];
        if (val && typeof val === 'object' && 'value' in val) {
          sanitizedFields[key] = val.value;
        } else {
          sanitizedFields[key] = val;
        }
      });
      
      // Backfill evidence_date_uploaded from createdAt metadata if empty
      if (kind === 'evidence' && !sanitizedFields['evidence_date_uploaded'] && itemData.createdAt) {
        sanitizedFields['evidence_date_uploaded'] = String(itemData.createdAt).slice(0, 10);
      }

      // Backfill calculated evidence date fields that may be empty in saved data
      if (kind === 'evidence') {
        const lastReviewed = sanitizedFields['evidence_last_reviewed'] as string | undefined;
        const freqMonths: Record<string, number> = {
          'Weekly': 0.25, 'Bi-weekly': 0.5, 'Monthly': 1,
          'Quarterly': 3, 'Bi-annually': 6, 'Annually': 12,
        };
        const calcNext = (from: string, freq: string): string | null => {
          const m = freqMonths[freq];
          if (!from || m === undefined) return null;
          const d = new Date(from);
          if (m < 1) { d.setDate(d.getDate() + Math.round(m * 28)); }
          else { d.setMonth(d.getMonth() + m); }
          return d.toISOString().split('T')[0];
        };
        if (lastReviewed) {
          const colFreq = sanitizedFields['collection_frequency'] as string | undefined;
          if (colFreq && !sanitizedFields['next_due_date']) {
            const next = calcNext(lastReviewed, colFreq);
            if (next) sanitizedFields['next_due_date'] = next;
          }
        }
      }

      setFieldValues(sanitizedFields);
    }
  }, [item, kind]);

  const groupedFields = useMemo(() => {
    // RegisterItemDetail has no fallback field list of its own (unlike ControlDetail.tsx's
    // getDefaultFields()) - it renders EXACTLY whatever FieldConfig rows the register's stored
    // config has, nothing more. Confirmed live: several evidence-register configs have been
    // left with only 1-2 real field rows (e.g. a lone read-only 'related_record_id' plus 'Last
    // Reviewed'), whether from an incomplete template application or leftover config for a
    // since-deleted register - and the create form then renders almost nothing, matching the
    // reported "Evidence details tab only shows Last review date" symptom exactly. Falling back
    // to the real, full evidence field set (confirmed live against a properly-configured
    // register, EVREG-cc3fc400) means a broken/missing config degrades to "the normal evidence
    // form" instead of "an unusable form with one field".
    const effectiveConfigs = (kind === 'evidence' && (fieldConfigs as FieldConfig[]).length === 0)
      ? DEFAULT_EVIDENCE_FIELDS
      : (fieldConfigs as FieldConfig[]);
    return effectiveConfigs.reduce(
      (acc: Record<number, FieldConfig[]>, config: FieldConfig) => {
        const cardNum = Number(config.cardNumber || 1);
        if (!acc[cardNum]) acc[cardNum] = [];
        acc[cardNum].push(config);
        return acc;
      },
      {}
    );
  }, [fieldConfigs, kind]);

  const metadataCardFields = useMemo(() => {
    if (kind !== 'evidence') return null;
    for (const fields of Object.values(groupedFields)) {
      const visible = fields.filter((f) => f.isVisible).sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
      if (visible.some((f) => f.fieldId === 'source_tab')) return visible;
    }
    return null;
  }, [groupedFields, kind]);

  // Per-card configs with each field's EFFECTIVE isEditable baked in - this is the single
  // source of truth both for what's rendered (the generic card branch and IdentityCard look
  // up their fields here) and for what's validated on save (see validableFieldConfigs below),
  // so the two can't silently drift apart.
  const effectiveFieldConfigsByCard = useMemo(() => {
    const result: Record<number, FieldConfig[]> = {};
    Object.entries(groupedFields).forEach(([cardNumStr, fields]) => {
      const cardNum = Number(cardNumStr);
      const visible = fields
        .filter((config) => config.isVisible)
        .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
      result[cardNum] = visible.map((config) => ({
        ...config,
        // Lock system fields from editing, even in edit mode
        isEditable: isEditing ? (config.isSystemField ? false : config.isEditable) : false,
      }));
    });
    return result;
  }, [groupedFields, isEditing]);

  // Cards whose fields render via bespoke hand-rolled markup with no FieldRenderer at all
  // ('schedule_status' -> CollectionScheduleCard, 'improvement_needed' -> ReviewNotesCard) -
  // there is nowhere to show an inline error for those, so they're excluded from the
  // required-field check entirely. 'source_tab' (the metadata card) IS validated, but through
  // its own effectiveMetadataCardFields below (a different, pre-existing override expression),
  // so it's excluded from the *generic* bucket here purely to avoid counting it twice.
  const cardNumbersExcludedFromGenericValidation = useMemo(() => {
    const result = new Set<number>();
    if (kind !== 'evidence') return result;
    const bypassOrSeparatelyHandledMarkers = ['schedule_status', 'improvement_needed', 'source_tab'];
    Object.entries(groupedFields).forEach(([cardNumStr, fields]) => {
      const visible = fields.filter((config) => config.isVisible);
      if (visible.some((config) => bypassOrSeparatelyHandledMarkers.includes(config.fieldId))) {
        result.add(Number(cardNumStr));
      }
    });
    return result;
  }, [groupedFields, kind]);

  // Metadata card's own effective configs - kept as a separate memo (rather than reusing
  // effectiveFieldConfigsByCard) because its existing override expression is subtly different
  // from the generic one: it has no outer `isEditing ? ... : false` guard. That's harmless in
  // practice (this card is only ever rendered while isEditing is true - see the JSX below), but
  // it's a pre-existing quirk, not something introduced here, so it's preserved as-is rather
  // than "fixed" to match the generic formula.
  const effectiveMetadataCardFields = useMemo(() => {
    if (!metadataCardFields) return null;
    return metadataCardFields.map((config) => ({
      ...config,
      isEditable: config.isSystemField ? false : config.isEditable,
    }));
  }, [metadataCardFields]);

  // The exact set of fields validated on save - the generic per-card effective configs (which
  // also cover the Identity card, since it looks up the same effectiveFieldConfigsByCard entry)
  // plus the metadata card's effective configs, minus whatever bypasses FieldRenderer entirely.
  const validableFieldConfigs = useMemo(() => {
    const generic = Object.entries(effectiveFieldConfigsByCard)
      .filter(([cardNumStr]) => !cardNumbersExcludedFromGenericValidation.has(Number(cardNumStr)))
      .flatMap(([, fields]) => fields);
    return [...generic, ...(effectiveMetadataCardFields || [])];
  }, [effectiveFieldConfigsByCard, cardNumbersExcludedFromGenericValidation, effectiveMetadataCardFields]);

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (itemId) return api.update(itemId, data);
      return api.create(data);
    },
    onSuccess: async (response) => {
      // Extract the item from response (could be { issue }, { incident }, etc.)
      const returnedItem = response?.[kind] || response?.item || response;
      
      // Immediately update field values with the returned data (includes calculated fields)
      if (returnedItem && typeof returnedItem === 'object') {
        const metadataKeys = ['issueId', 'incidentId', 'remediationId', 'threatId', 'issue_id', 'incident_id', 'remediation_id', 'threat_id', 'tenantId', 'registerId', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'fields'];
        const updatedFields: Record<string, any> = {};
        
        // First, extract nested fields from the 'fields' object if it exists
        if (returnedItem.fields && typeof returnedItem.fields === 'object') {
          Object.keys(returnedItem.fields).forEach((key) => {
            updatedFields[key] = returnedItem.fields[key];
          });
        }
        
        // Then extract root-level fields (these override nested fields if same key)
        Object.keys(returnedItem).forEach((key) => {
          if (!metadataKeys.includes(key)) {
            updatedFields[key] = returnedItem[key];
          }
        });
        
        setFieldValues((prev) => ({ ...prev, ...updatedFields }));
      }
      
      // Invalidate the detail query
      queryClient.invalidateQueries({ queryKey: [kind, itemId] });
      
      // Invalidate the list query to show new items
      queryClient.invalidateQueries({ queryKey: ['list'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] }); // gap analysis / reports
      
      setIsEditing(false);
      toast.success(`${title} updated.`);
      
      // If creating a new item, redirect to its detail page
      if (!itemId && response) {
        const newId = response[`${kind}Id`] || response.id || response.item?.[`${kind}Id`];
        if (newId) {
          // Auto-link to a control if the page was opened via Raise button
          if (autoLinkControlId) {
            try {
              await linkedArtefactsApi.createLinks({
                source_id: autoLinkControlId,
                source_type: 'control',
                source_register: autoLinkControlRegister,
                target_artefacts: [{ id: newId, type: kind, register: registerId }],
              });
              toast.success(`Linked to control ${autoLinkControlId}`);
            } catch (err) {
              console.warn('Auto-link to control failed:', err);
              toast.warning('Item saved but auto-link to control failed — link it manually in the Linked Artefacts tab.');
            }
          }
          // Auto-link to a risk if the page was opened via Raise button
          if (autoLinkRiskId) {
            try {
              await linkedArtefactsApi.createLinks({
                source_id: autoLinkRiskId,
                source_type: 'risk',
                source_register: autoLinkRiskRegister,
                target_artefacts: [{ id: newId, type: kind, register: registerId }],
              });
              toast.success(`Linked to risk ${autoLinkRiskId}`);
            } catch (err) {
              console.warn('Auto-link to risk failed:', err);
              toast.warning('Item saved but auto-link to risk failed — link it manually in the Linked Artefacts tab.');
            }
          }
          // Handle irregular plurals (evidence route is /evidence/:id not /evidences/:id)
          const kindPathMap: Record<string, string> = { evidence: 'evidence' };
          const kindPath = kindPathMap[kind] ?? `${kind}s`;
          window.location.href = `/${kindPath}/${newId}?registerId=${registerId}`;
        }
      }
    },
    onError: (error: Error) => {
      console.error('Save error:', error);
      toast.error(`Failed to save ${title.toLowerCase()}: ${error.message}`);
    },
  });

  // Frequency string → number of months
  const frequencyToMonths: Record<string, number> = {
    'Weekly': 0.25,
    'Bi-weekly': 0.5,
    'Monthly': 1,
    'Quarterly': 3,
    'Bi-annually': 6,
    'Annually': 12,
  };

  const calcDateFromFrequency = (fromDate: string, frequency: string): string | null => {
    const months = frequencyToMonths[frequency];
    if (!fromDate || months === undefined) return null;
    const date = new Date(fromDate);
    if (months < 1) {
      date.setDate(date.getDate() + Math.round(months * 28));
    } else {
      date.setMonth(date.getMonth() + months);
    }
    return date.toISOString().split('T')[0];
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues((prev) => {
      const updated = { ...prev, [fieldId]: value };

      if (kind === 'evidence') {
        const lastReviewed = fieldId === 'evidence_last_reviewed' ? value : prev['evidence_last_reviewed'];

        // collection_frequency + evidence_last_reviewed → next_due_date
        if (fieldId === 'evidence_last_reviewed' || fieldId === 'collection_frequency') {
          const freq = fieldId === 'collection_frequency' ? value : prev['collection_frequency'];
          const next = calcDateFromFrequency(lastReviewed as string, freq as string);
          if (next) updated['next_due_date'] = next;
        }
      }

      return updated;
    });

    if (invalidFieldIds.has(fieldId)) {
      setInvalidFieldIds((prev) => {
        const next = new Set(prev);
        next.delete(fieldId);
        return next;
      });
    }
  };

  const handleSave = () => {
    if (!registerId) {
      toast.warning('Missing registerId in URL. Open this page with ?registerId=...');
      return;
    }

    const missing = getMissingRequiredFields(validableFieldConfigs, fieldValues);
    if (missing.length > 0) {
      setInvalidFieldIds(new Set(missing));
      const names = validableFieldConfigs.filter((c) => missing.includes(c.fieldId)).map((c) => c.fieldName).join(', ');
      toast.error(`Please fill in required fields: ${names}`);
      return;
    }
    setInvalidFieldIds(new Set());

    const dataToSave: Record<string, any> = { ...fieldValues };

    (fieldConfigs as FieldConfig[]).forEach((config) => {
      if (config.isCalculated || !config.isEditable) {
        delete dataToSave[config.fieldId];
      }
    });

    // Properly nest fields under 'fields' property (Lambda expects this structure)
    const payload = {
      registerId,
      tenantId: getTenantId(),
      fields: dataToSave, // Nest all fields under 'fields' property
    };

    saveMutation.mutate(payload);
  };

  if (!registerId) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          Missing <strong>registerId</strong> query param. Example: <code>#/.../new?registerId=REG-123</code>
        </div>
      </div>
    );
  }

  if (isLoadingConfigs || (itemId && isLoadingItem)) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '400px' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (configsError) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          Failed to load field configuration for this register.
        </div>
      </div>
    );
  }

  if (itemId && itemError) {
    console.error('Item load error:', itemError);
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          <span>
            Failed to load {title.toLowerCase()}: {(itemError as Error).message}
            <br /><br />
            <strong>Debug info:</strong>
            <pre>{JSON.stringify({ itemId, kind, error: (itemError as Error).message }, null, 2)}</pre>
          </span>
        </div>
      </div>
    );
  }

  if (itemError) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load {title.toLowerCase()}.</div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <div className="np-page-head" style={{ alignItems: 'flex-start' }}>
        {kind === 'evidence' && itemId ? (
          <div className="d-flex align-items-start gap-3" style={{ minWidth: 0 }}>
            <span className="np-icon-badge np-tone-primary flex-shrink-0">
              <i className={iconClass}></i>
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="np-sub" style={{ marginBottom: 4 }}>{itemId}</div>
              <h1 className="text-truncate">
                {String(fieldValues['evidence_title'] || fieldValues.title || title)}
              </h1>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {!!fieldValues['evidence_status'] && (
                  <span
                    className={`np-chip np-tone-${
                      /active|approved|accepted/i.test(String(fieldValues['evidence_status'])) ? 'success' : 'neutral'
                    }`}
                  >
                    {String(fieldValues['evidence_status'])}
                  </span>
                )}
                {!!fieldValues['type'] && <span className="np-chip np-tone-neutral">{String(fieldValues['type'])}</span>}
                {Array.isArray(fieldValues['linked_artefacts']) && (fieldValues['linked_artefacts'] as any[]).length > 0 && (
                  <span className="np-chip np-tone-neutral">
                    <i className="ri-links-line"></i>
                    {(fieldValues['linked_artefacts'] as any[]).length} linked
                  </span>
                )}
                {accountablePerson && (
                  <span className="np-chip np-tone-neutral">
                    <i className="ri-user-line"></i>
                    {/* Person's real shape (firstName/lastName, not personName) isn't reflected
                        in the stale Person TS type - same cast-to-any workaround already used
                        elsewhere in this file for the same reason. */}
                    {[(accountablePerson as any).firstName, (accountablePerson as any).lastName].filter(Boolean).join(' ').trim()
                      || accountablePerson.personName
                      || (accountablePerson as any).email
                      || (accountablePerson as any).personId}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-3">
            <span className="np-icon-badge np-tone-primary flex-shrink-0">
              <i className={iconClass}></i>
            </span>
            <div>
              <h1>{itemId ? `Edit ${title}` : `New ${title}`}</h1>
              {itemId && (
                <div className="d-flex align-items-center gap-2">
                  <span className="np-chip np-tone-neutral np-tabular">{itemId}</span>
                  {(() => {
                    const t = fieldValues[`${kind}_title`] || fieldValues.title || fieldValues.name
                      || fieldValues[`${kind}_name`] || fieldValues[`${kind}_subject`] || fieldValues.subject;
                    return t ? <span className="np-sub">{String(t)}</span> : null;
                  })()}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="np-page-actions">
          {!isEditing ? (
            <>
              <button className="np-btn np-btn-primary" onClick={() => { setIsEditing(true); setInvalidFieldIds(new Set()); }}>
                <i className="ri-edit-line"></i>
                Edit
              </button>
              <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
            </>
          ) : (
            <>
              <button
                className="np-btn np-btn-primary"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Save
                  </>
                )}
              </button>
              {itemId && (
                <button
                  className="np-btn np-btn-ghost"
                  onClick={() => {
                    setIsEditing(false);
                    setInvalidFieldIds(new Set());
                    // Handle nested structure: item = {issue: {...}} or {incident: {...}}
                    const itemData = item?.[kind] || item;
                    if (itemData?.fields) {
                      // Sanitize field values when canceling
                      const sanitizedFields: FieldValues = {};
                      Object.keys(itemData.fields).forEach((key) => {
                        const val = itemData.fields[key];
                        if (val && typeof val === 'object' && 'value' in val) {
                          sanitizedFields[key] = val.value;
                       } else {
                          sanitizedFields[key] = val;
                        }
                      });
                      setFieldValues(sanitizedFields);
                    }
                  }}
                >
                  <i className="ri-close-line"></i>
                  Cancel
                </button>
              )}
              <button className="np-btn np-btn-ghost" onClick={() => window.history.back()}>
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Tab navigation ── */}
      <div className="np-tabs" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
        {([
          { id: 'details', label: `${title} Details`, icon: 'ri-file-list-3-line', count: null as number | null },
          { id: 'attachments', label: 'Attachments', icon: 'ri-attachment-2', count: null as number | null },
          { id: 'raci', label: 'RACI', icon: 'ri-team-line', count: kind === 'evidence' ? (heroRaci as any[]).length || null : null },
          ...(enabledModules.requirementsTracker && (kind === 'issue' || kind === 'incident' || kind === 'remediation')
            ? [{ id: 'requirements', label: 'Requirements', icon: 'ri-task-line', count: null as number | null }]
            : []),
          {
            id: 'artefacts',
            label: 'Linked Artefacts',
            icon: 'ri-links-line',
            count: Array.isArray(fieldValues['linked_artefacts']) ? (fieldValues['linked_artefacts'] as any[]).length || null : null,
          },
          ...extraTabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
            icon: tab.icon,
            count: tab.id === 'schedule' ? submissionsCount : null,
          })),
        ]).map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`np-tab d-flex align-items-center gap-1${activeTab === tab.id ? ' active' : ''}`}
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={tab.icon}></i>
            {tab.label}
            {!!tab.count && (
              <span className="np-chip np-tone-neutral np-tabular" style={{ fontSize: '0.65rem' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'details' && (
        <>
      <div className="row">
        {Object.keys(groupedFields)
          .sort((a, b) => Number(a) - Number(b))
          .map((cardNum) => {
            // Effective configs (isEditable already resolved) - the same array is used to
            // render this card AND (for cards that fall through to the generic branch or
            // IdentityCard below) to validate required fields on save.
            const cardFields = effectiveFieldConfigsByCard[Number(cardNum)] || [];

            if (cardFields.length === 0) return null;

            // The Collection Schedule card gets a bespoke layout (evidence only) -
            // detected by field content, not the card's display name, so a rename
            // doesn't silently fall back to the generic grid.
            if (kind === 'evidence' && cardFields.some((c) => c.fieldId === 'schedule_status')) {
              return (
                <CollectionScheduleCard
                  key={cardNum}
                  fieldValues={fieldValues}
                  onChange={handleFieldChange}
                  isEditing={isEditing}
                  evidenceId={itemId || ''}
                  submissions={evidenceSubmissions}
                />
              );
            }

            // Identity: compact key/value grid instead of stacked labelled inputs (evidence only).
            if (kind === 'evidence' && cardFields.some((c) => c.fieldId === 'evidence_code')) {
              return (
                <IdentityCard
                  key={cardNum}
                  cardFields={cardFields}
                  fieldValues={fieldValues}
                  isEditing={isEditing}
                  onChange={handleFieldChange}
                  people={people}
                  assets={assets}
                  departments={departments}
                  invalidFieldIds={invalidFieldIds}
                />
              );
            }

            // Review Notes: icon-labeled annotation blocks instead of bare textareas (evidence only).
            if (kind === 'evidence' && cardFields.some((c) => c.fieldId === 'improvement_needed')) {
              return (
                <ReviewNotesCard
                  key={cardNum}
                  cardFields={cardFields}
                  fieldValues={fieldValues}
                  isEditing={isEditing}
                  onChange={handleFieldChange}
                />
              );
            }

            // Metadata: rendered as a minimal footer strip below the grid, not a full card (evidence only).
            if (kind === 'evidence' && cardFields.some((c) => c.fieldId === 'source_tab')) {
              return null;
            }

            // Sanitize card name - extract string value if it's an object
            const rawCardName = cardDefinitions[String(cardNum)] || cardFields[0]?.cardName;
            const cardName = typeof rawCardName === 'object' && rawCardName && 'value' in rawCardName
              ? String((rawCardName as any).value)
              : String(rawCardName || `Card ${cardNum}`);

            return (
              <div key={cardNum} className="col-12 mb-4">
                <div className="np-card">
                  <div className="np-card-head">
                    <h2>{cardName}</h2>
                  </div>
                  <div className="np-card-body">
                    {cardFields.map((config) => (
                      <FieldRenderer
                        key={config.fieldId}
                        config={config}
                        value={fieldValues[config.fieldId]}
                        onChange={handleFieldChange}
                        people={people}
                        assets={assets}
                        departments={departments}
                        error={invalidFieldIds.has(config.fieldId) ? 'This field is required.' : undefined}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Metadata: minimal footer strip, not a full card (evidence only) */}
      {metadataCardFields && !isEditing && (
        <div className="d-flex flex-wrap gap-4 np-sub mb-4 px-1">
          {metadataCardFields.map((config) => {
            const value = String(fieldValues[config.fieldId] ?? '').trim();
            if (!value) return null;
            const icon =
              config.fieldId === 'evidence_uploaded_by'
                ? 'ri-user-line'
                : config.fieldId === 'evidence_date_uploaded'
                ? 'ri-calendar-line'
                : 'ri-file-copy-2-line';
            const text =
              config.fieldId === 'evidence_uploaded_by'
                ? `Uploaded by ${value}`
                : config.fieldId === 'evidence_date_uploaded'
                ? (() => {
                    const d = new Date(value);
                    return isNaN(d.getTime())
                      ? value
                      : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                  })()
                : `Imported from ${value}`;
            return (
              <span key={config.fieldId} className="d-flex align-items-center gap-1">
                <i className={icon}></i>
                {text}
              </span>
            );
          })}
        </div>
      )}
      {metadataCardFields && isEditing && (
        <div className="col-12 mb-4">
          <div className="np-card">
            <div className="np-card-head">
              <h2>Metadata</h2>
            </div>
            <div className="np-card-body">
              {(effectiveMetadataCardFields || []).map((config) => (
                <FieldRenderer
                  key={config.fieldId}
                  config={config}
                  value={fieldValues[config.fieldId]}
                  onChange={handleFieldChange}
                  people={people}
                  assets={assets}
                  departments={departments}
                  error={invalidFieldIds.has(config.fieldId) ? 'This field is required.' : undefined}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="row">
        {itemId ? (
          <></>
        ) : (
          <></>
        )}
      </div>
      </>
      )}

      {activeTab === 'requirements' && enabledModules.requirementsTracker && (kind === 'issue' || kind === 'incident' || kind === 'remediation') && (
        <div className="row">
          {itemId ? (
            <RequirementsTracker
              entityType={kind}
              entityId={itemId}
              functionalRequirements={(fieldValues[`${kind}_functional_requirements`] as any) || []}
              nonFunctionalRequirements={(fieldValues[`${kind}_nonfunctional_requirements`] as any) || []}
              onUpdate={(functional, nonFunctional) => {
                setFieldValues((prev) => ({
                  ...prev,
                  [`${kind}_functional_requirements`]: functional,
                  [`${kind}_nonfunctional_requirements`]: nonFunctional,
                }));
              }}
              people={people}
            />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-task-line"></i> Requirements</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Available after you save this {title.toLowerCase()}.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'attachments' && enabledModules.documentManager && (
        <div className="row">
          {itemId ? (
            <DocumentManager entityType={kind} entityId={itemId} readOnly={!isEditing} />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-attachment-2"></i> Attachments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Available after you save this {title.toLowerCase()}.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'raci' && enabledModules.raciManager && (
        <div className="row">
          {itemId ? (
            <RaciAssignments artefactId={itemId} artefactType={kind} readOnly={!isEditing} />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-team-line"></i> RACI Assignments</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Available after you save this {title.toLowerCase()}.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'artefacts' && enabledModules.linkedArtefacts && (
        <div className="row">
          {itemId ? (
            <LinkedArtefacts
              artefactId={itemId}
              artefactType={kind}
              artefactRegister={`${kind}_register`}
              registerId={registerId}
              readOnly={!isEditing}
            />
          ) : (
            <div className="col-12 mb-4">
              <div className="np-card">
                <div className="np-card-head">
                  <h2><i className="ri-links-line"></i> Linked Artefacts</h2>
                </div>
                <div className="np-card-body">
                  <p className="np-sub mb-0">Available after you save this {title.toLowerCase()}.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {extraTabs.map((tab) =>
        activeTab === tab.id ? (
          <div key={tab.id}>{tab.content(itemId)}</div>
        ) : null
      )}
    </div>
  );
};
